import type { ScopeSelection } from "@/store/settingsStore";

/**
 * Query params for a scope selection.
 *
 * Sent comma-separated rather than as repeated keys: axios serialises arrays as
 * `legalEntityIds[]=a&legalEntityIds[]=b`, which Spring will not bind to a
 * `List<String>`. A CSV value binds cleanly and keeps the URL readable.
 *
 * Empty axes are omitted entirely — an empty `legalEntityIds=` would reach the backend
 * as a blank id rather than as "no scope requested".
 */
export function scopeParams(scope: ScopeSelection | null | undefined): {
  legalEntityIds?: string;
  branchIds?: string;
} {
  if (!scope) return {};
  const params: { legalEntityIds?: string; branchIds?: string } = {};
  if (scope.legalEntityIds.length > 0) params.legalEntityIds = scope.legalEntityIds.join(",");
  if (scope.branchIds.length > 0) params.branchIds = scope.branchIds.join(",");
  return params;
}

/** True when the selection narrows anything — drives the "filter applied" highlight. */
export function isScopeApplied(scope: ScopeSelection | null | undefined): boolean {
  return Boolean(scope && (scope.legalEntityIds.length > 0 || scope.branchIds.length > 0));
}

/** How many individual picks the selection holds, for the Filters badge. */
export function scopeSize(scope: ScopeSelection | null | undefined): number {
  if (!scope) return 0;
  return scope.legalEntityIds.length + scope.branchIds.length;
}

export function sameScope(a: ScopeSelection, b: ScopeSelection): boolean {
  const same = (x: string[], y: string[]) => x.length === y.length && x.every((v, i) => v === y[i]);
  return same(a.legalEntityIds, b.legalEntityIds) && same(a.branchIds, b.branchIds);
}
